import {
  ApplicationProductTypeEnum,
  VlocPageId,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen, {
  SectionedContentScreenModel,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponent, VXLComponentId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import {
  showZendeskSection,
  ZendeskSections,
} from 'shared/utils/zendesk/zendeskUtils';

import useGetLineOfCreditAutoPayStatus from './hooks/useGetLineOfCreditAutoPayStatus';

const ManageAccountScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'manageAccount',
  });

  const { loading: isLoading, errorModel } = useAgreements([
    ApplicationProductTypeEnum.LineOfCredit,
  ]);

  const { bottom } = useSafeAreaInsets();

  const { fetching, error, isAutopayEnabled, refetch } =
    useGetLineOfCreditAutoPayStatus();
  const hasFocusedBefore = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const { accountId = '', isActiveAccount = '' } = useLocalSearchParams<{
    accountId: string;
    isActiveAccount: string;
  }>();

  const { accountStatementsDetails = [] } = useAccountStatements({
    accountId: '',
  });

  const vlocAccount = accountStatementsDetails?.find(
    (account) => account?.name === 'Varo Line Of Credit Account',
  );

  const getTrailingAccessory = () => {
    return {
      id: 'vloc-manage-account-autopay-tag',
      name: VXLComponentId.TAG,
      props: {
        text: isAutopayEnabled
          ? translatedText('autopayStatus.on')
          : translatedText('autopayStatus.off'),
        type: isAutopayEnabled ? 3 : 5,
      },
    };
  };

  const sectionsData: SectionedContentScreenModel = { sections: [] };

  const manageAccountSectionHeader = (): VXLNode => {
    return {
      id: 'review-deposit-page-section-header',
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: translatedText('manageAccountHeader'),
      },
    };
  };

  const showFaqArticle = () => {
    showZendeskSection(ZendeskSections.VARO_VLOC_FAQ);
  };

  const showEduScreen = () => {
    router.push({
      pathname: '/varo-line-of-credit/eligibility',
      params: {
        educationalScreenId: VlocPageId.VlocEduScreenInformation,
      },
    });
  };

  const accountItems: VXLComponent[] = [];
  if (isActiveAccount === 'true' && isAutopayEnabled !== undefined) {
    accountItems.push({
      id: 'manage-account-autopay-list-item',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: translatedText('autoPay'),
        secondaryCopy: '',
        trailingAccessory: getTrailingAccessory(),
        analyticsId: 'auto-pay',
        customAttributes: {
          autopay_status: isAutopayEnabled ? 'on' : 'off',
          account_type: 'vloc',
        },
        onPress: () =>
          router.navigate({
            pathname: '/varo-line-of-credit/autopay',
            params: { autoPayEnabled: isAutopayEnabled.toString() },
          }),
      },
    });
  }
  accountItems.push({
    id: 'manage-account-varo-line-of-credit-history-list-item',
    name: VXLComponentId.LIST_ITEM,
    props: {
      primaryCopy: translatedText('varoLineOfCreditHistory'),
      secondaryCopy: '',
      analyticsId: 'history',
      customAttributes: {
        account_type: 'vloc',
      },
      onPress: () =>
        router.navigate({
          pathname: '/varo-line-of-credit/history',
          params: { id: accountId },
        }),
    },
  });
  accountItems.push({
    id: 'manage-account-statements-and-documents-list-item',
    name: VXLComponentId.LIST_ITEM,
    props: {
      primaryCopy: translatedText('statementsAndDocuments'),
      secondaryCopy: '',
      analyticsId: 'statements-documents',
      onPress: () => {
        router.navigate({
          pathname: `/myvaro/statements-and-forms/account-statements/[id]`,
          params: {
            id: vlocAccount?.id ?? accountId,
            category: 'vloc',
            account_type: 'vloc',
          },
        });
      },
    },
  });

  sectionsData.sections.push({
    sectionHeader: manageAccountSectionHeader(),
    data: accountItems,
  });

  const helpSectionHeader = (): VXLNode => {
    return {
      id: 'help-section-header',
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: translatedText('helpSectionHeader'),
      },
    };
  };

  const helpItems: VXLComponent[] = [];
  helpItems.push({
    id: 'what-is-vloc-list-item',
    name: VXLComponentId.LIST_ITEM,
    props: {
      primaryCopy: translatedText('whatIsVaroLineOfCredit'),
      secondaryCopy: '',
      analyticsId: 'faq',
      customAttributes: {
        faq_name: 'vloc_intro',
      },
      onPress: showEduScreen,
    },
  });

  helpItems.push({
    id: 'vloc-faq-list-item',
    name: VXLComponentId.LIST_ITEM,
    props: {
      primaryCopy: translatedText('varoLineCreditFaqs'),
      secondaryCopy: '',
      analyticsId: 'faq-intro',
      customAttributes: {
        faq_name: 'vloc_faqs',
      },
      onPress: showFaqArticle,
    },
  });

  helpItems.push({
    id: 'vloc-tnc-list-item',
    name: VXLComponentId.LIST_ITEM,
    props: {
      primaryCopy: translatedText('termsAndConditions'),
      secondaryCopy: '',
      analyticsId: 'faq-tnc',
      customAttributes: {
        faq_name: 'terms_and_conditions',
      },
      onPress: () =>
        router.navigate({
          pathname: '/terms-and-conditions',
          params: {
            agreementType: ApplicationProductTypeEnum.LineOfCredit,
            title: translatedText('termsAndConditions'),
          },
        }),
    },
  });

  sectionsData.sections.push({
    sectionHeader: helpSectionHeader(),
    data: helpItems,
  });

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(error ? goHome : goBack);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('screenTitle'),
          headerBackVisible: !error,
          headerShown: !error,
          gestureEnabled: !error,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching || isLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error || errorModel ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-manageAccountScreen"
              trackingAnalyticsId="manage-account-error-screen-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_MANAGE_ACCOUNT,
              }}
            />
          ) : null
        }
      />
      <SectionedContentScreen model={sectionsData} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ManageAccountScreen;
